import React, { useState, useRef } from 'react';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

interface AdminImageUploadProps {
  label?: string;
  value: string;
  onChange: (url: string) => void;
  folder?: string;
  className?: string;
}

export const AdminImageUpload: React.FC<AdminImageUploadProps> = ({
  label = 'Imagen',
  value,
  onChange,
  folder = 'landing',
  className = '',
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const cleanName = file.name.replace(/[^a-zA-Z0-9]/g, '_');
      const storageRef = ref(storage, `${folder}/${Date.now()}_${cleanName}.${ext}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(snapshot.ref);
      onChange(downloadUrl);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error al subir la imagen a Firebase Storage.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={`p-3 bg-purple-950/20 border border-purple-500/30 rounded-xl space-y-2.5 text-left ${className}`}>
      {label && (
        <div className="flex items-center justify-between">
          <label className="text-xs font-semibold text-purple-300 uppercase tracking-wider flex items-center gap-1.5">
            <i className="ri-image-edit-line text-sm" />
            {label}
          </label>
          {uploading && (
            <span className="text-xs text-cyan-400 flex items-center gap-1">
              <i className="ri-loader-4-line animate-spin" /> Subiendo...
            </span>
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        {value ? (
          <img
            src={value}
            alt="Preview"
            className="w-14 h-14 rounded-lg object-cover border border-white/20 flex-shrink-0 bg-black/40"
          />
        ) : (
          <div className="w-14 h-14 rounded-lg border border-dashed border-white/20 flex items-center justify-center text-white/30 flex-shrink-0 bg-black/30">
            <i className="ri-image-line text-xl" />
          </div>
        )}

        <div className="flex-1 space-y-1.5 min-w-0">
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="https://..."
            className="w-full bg-black/40 border border-white/15 rounded-lg px-2.5 py-1.5 text-xs text-white placeholder-white/25 focus:outline-none focus:border-purple-500"
          />
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              className="hidden"
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="px-2.5 py-1 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-xs rounded font-medium transition-colors flex items-center gap-1 cursor-pointer"
            >
              <i className="ri-upload-cloud-line" />
              Subir Archivo
            </button>
            <span className="text-[11px] text-white/40">PNG, JPG, WebP</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminImageUpload;
